import React, { useEffect, useMemo, useState } from "react"
import { Container, Card, CardBody, Table, Button, Input } from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
  getFleetExpenseSettings, saveFleetExpenseSettings,
  getFleetCostLedgerSettings, saveFleetCostLedgerSettings,
} from "../../services/fleet"
import { toastError, toastSuccess } from "../../helpers/msg_helper"

// account_groups.type → nature of account
const NATURE = { 1: "Assets", 2: "Liabilities", 3: "Income", 4: "Expenses" }

/**
 * Developer → Fleet Accounts.
 * Section 1 — which finance groups are the "vehicle expense" groups (their ledgers become the
 *   Expense-account choices on the vehicle form). Stored as group IDs.
 * Section 2 — a COMMON (shared, applies to every company) ledger for each fleet cost head. Fleet
 *   posts to the shared/common book, so it's one assignment, not one per company. Dropdowns list
 *   only common (company_id NULL) ledgers under the groups above. Auto-filled by name; editable.
 * Section 3 — VAT mode + the common VAT-input ledger.
 */
const FleetAccounts = () => {
  document.title = "Fleet Accounts | Developer"
  const [groups, setGroups] = useState([])
  const [selected, setSelected] = useState([]) // group ids
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Section 2 + 3 (common)
  const [cost, setCost] = useState({ categories: [], ledgers: [], mapping: {}, vat_ledgers: [], has_groups: true })
  const [mapping, setMapping] = useState({}) // { categoryKey: ledgerId }
  const [vatMode, setVatMode] = useState("inclusive")
  const [vatLedger, setVatLedger] = useState("")

  const loadAll = async () => {
    setLoading(true)
    try {
      const [g, c] = await Promise.all([getFleetExpenseSettings(), getFleetCostLedgerSettings()])
      setGroups(g?.data?.groups || [])
      setSelected((g?.data?.selected || []).map(Number))
      const cd = c?.data || { categories: [], ledgers: [], mapping: {}, vat_ledgers: [], has_groups: true }
      setCost(cd)
      setMapping(cd.mapping || {})
      setVatMode(cd.vat_mode || "inclusive")
      setVatLedger(cd.vat_ledger || "")
    } catch (e) {
      toastError(e?.message || "Failed to load fleet accounts")
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { loadAll() }, [])

  const toggle = id =>
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))

  const setCell = (key, val) =>
    setMapping(m => ({ ...m, [key]: val ? Number(val) : null }))

  const save = async () => {
    setSaving(true)
    try {
      await saveFleetExpenseSettings(selected)
      await saveFleetCostLedgerSettings({ mapping, vat_mode: vatMode, vat_ledger: vatLedger || null })
      toastSuccess("Fleet accounts saved")
      await loadAll() // re-derive available ledgers / auto-fill after a group change
    } catch (e) {
      toastError(e?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const q = search.trim().toLowerCase()
  const filtered = useMemo(
    () => groups.filter(g => !q || (g.name || "").toLowerCase().includes(q) || (NATURE[g.type] || "").toLowerCase().includes(q)),
    [groups, q]
  )

  // every cost head lists ALL related common fleet ledgers — you pick the right one per row
  const optionsFor = () => cost.ledgers || []

  return (
    <div className="page-content report-page tnt-scope">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <Breadcrumbs
            title="Developer"
            breadcrumbObject={[
              { title: "Developer", link: "/developer" },
              { title: "Fleet Accounts", link: "#" },
            ]}
          />
          <Button color="primary" size="sm" className="mb-3" onClick={save} disabled={saving || loading}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>

        {/* Section 1 — vehicle expense groups */}
        <Card>
          <CardBody>
            <h6 className="fw-semibold mb-1">1 · Vehicle expense groups</h6>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2 gap-2">
              <div className="text-muted small" style={{ maxWidth: 640 }}>
                Tick the finance groups whose ledgers should appear as the <b>Expense account</b> on the
                vehicle form, and feed the common cost-ledger dropdowns below.
                <b> {selected.length}</b> group{selected.length === 1 ? "" : "s"} selected.
              </div>
              <Input
                bsSize="sm"
                style={{ maxWidth: 240 }}
                placeholder="Search groups…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 620 }}>
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Use</th>
                    <th>Group Name</th>
                    <th style={{ width: 160 }}>Nature</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="3" className="text-center text-muted py-4">Loading…</td></tr>}
                  {!loading && !filtered.length && <tr><td colSpan="3" className="text-center text-muted py-4">No groups found.</td></tr>}
                  {!loading && filtered.map(g => (
                    <tr key={g.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selected.includes(Number(g.id))}
                          onChange={() => toggle(Number(g.id))}
                        />
                      </td>
                      <td className="fw-500">{g.name}</td>
                      <td className="text-muted">{NATURE[g.type] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Section 2 — common cost ledgers */}
        <Card className="mt-3">
          <CardBody>
            <h6 className="fw-semibold mb-1">2 · Cost ledgers (common — applies to all companies)</h6>
            <div className="text-muted small mb-2" style={{ maxWidth: 780 }}>
              Pick the <b>common</b> (shared, <code>company_id NULL</code>) ledger each fleet cost posts
              to. The <b>fuel-entry auto-voucher</b> debits the <b>Worker/Staff · Fuel</b> rows; the rest
              are captured for future cost entries. Dropdowns list only common ledgers under the groups
              ticked above — auto-filled by name where possible.
            </div>

            {!cost.ledgers.length && !loading ? (
              <div className="alert alert-warning py-2 px-3 small mb-0">
                No <b>common</b> ledgers found under the ticked expense groups. Create the shared fleet
                ledgers (with <code>company_id NULL</code>), then they'll appear here to assign.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 260 }}>Cost head</th>
                      <th>Common ledger</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cost.categories.map(cat => {
                      const opts = optionsFor(cat.side)
                      const isFuel = cat.key === "worker_fuel" || cat.key === "staff_fuel"
                      return (
                        <tr key={cat.key}>
                          <td className={isFuel ? "fw-600" : "fw-500"}>
                            {cat.label}
                            {isFuel && <span className="badge bg-soft-primary text-primary ms-2" style={{ fontSize: 10 }}>used by fuel entry</span>}
                          </td>
                          <td>
                            <Input
                              type="select" bsSize="sm"
                              value={mapping?.[cat.key] ?? ""}
                              onChange={e => setCell(cat.key, e.target.value)}
                            >
                              <option value="">— not set —</option>
                              {opts.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                            </Input>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Section 3 — VAT */}
        <Card className="mt-3">
          <CardBody>
            <h6 className="fw-semibold mb-1">3 · VAT</h6>
            <div className="text-muted small mb-3" style={{ maxWidth: 780 }}>
              How the fuel-entry auto-voucher handles VAT on the entered amount, and which common ledger
              the input VAT posts to (auto-detected from the shared <i>Duties &amp; Taxes</i> group;
              override if needed).
            </div>

            <div className="mb-3" style={{ maxWidth: 420 }}>
              <label className="text-muted small d-block mb-1">VAT mode</label>
              <Input type="select" bsSize="sm" value={vatMode} onChange={e => setVatMode(e.target.value)}>
                <option value="inclusive">Inclusive — amount includes 5% VAT (split it out)</option>
                <option value="exclusive">Exclusive — add 5% VAT on top of the amount</option>
                <option value="none">No VAT — post the full amount, no VAT line</option>
              </Input>
            </div>

            {vatMode !== "none" && (
              <div style={{ maxWidth: 560 }}>
                <label className="text-muted small d-block mb-1">VAT input ledger (common)</label>
                <Input
                  type="select" bsSize="sm"
                  value={vatLedger ?? ""}
                  onChange={e => setVatLedger(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">— auto (Duties &amp; Taxes) —</option>
                  {(cost.vat_ledgers || []).map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </Input>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default FleetAccounts
